import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import client from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { FeedPathTag } from "@/components/FeedPathTag";
import { Pencil, Save, Plus, Trash2, Users, Upload, ImageOff, RefreshCw } from "lucide-react";
import { VersionTag } from "@/components/VersionTag";

const NO_DEPARTMENT = "__none__";
const EMPTY_DRAFT = {
  name: "", title: "", department: "", department_id: "", phone: "", mail: "", pos: "", pic: "", pic_big: "", langs: "",
};

const DEFAULT_MAX_SIZE_MB = 5;
const DEFAULT_ALLOWED_FORMATS = ["jpeg", "png", "webp", "gif"];
const FORMAT_MIME = { jpeg: "image/jpeg", png: "image/png", webp: "image/webp", gif: "image/gif" };

function FormField({ label, feedPath, className = "", children }) {
  return (
    <div className={className}>
      <Label className="data-label mb-1 flex items-center gap-1">
        {label}
        <FeedPathTag path={feedPath} />
      </Label>
      {children}
    </div>
  );
}

function EmployeeForm({ draft, setDraft, disabled, departments }) {
  const { t, lang } = useI18n();
  const departmentLabel = (dep) => (lang === "de" ? dep.name_de : dep.name_en);
  const selectDepartment = (value) => {
    setDraft({ ...draft, department_id: value === NO_DEPARTMENT ? "" : value });
  };
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      <FormField label={t("employees.name")} feedPath="Dealer.Employees.Employee.name" className="md:col-span-2">
        <Input data-testid="employee-form-name" disabled={disabled} value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
      </FormField>
      <FormField label={t("employees.jobTitle")} feedPath="Dealer.Employees.Employee.title">
        <Input data-testid="employee-form-title" disabled={disabled} value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
      </FormField>
      <FormField label={t("employees.department")}>
        <Select disabled={disabled} value={draft.department_id || NO_DEPARTMENT} onValueChange={selectDepartment}>
          <SelectTrigger data-testid="employee-form-department">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_DEPARTMENT}>{t("employees.departmentNone")}</SelectItem>
            {departments.map((dep) => (
              <SelectItem key={dep.id} value={dep.id} data-testid={`employee-form-department-${dep.id}`}>
                {departmentLabel(dep)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>
      <FormField label={t("employees.departmentImported")} feedPath="Dealer.Employees.Employee.department">
        <Input data-testid="employee-form-department-imported" disabled readOnly value={draft.department} />
      </FormField>
      <FormField label={t("employees.phone")} feedPath="Dealer.Employees.Employee.phone">
        <Input data-testid="employee-form-phone" disabled={disabled} value={draft.phone}
          onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
      </FormField>
      <FormField label={t("employees.email")} feedPath="Dealer.Employees.Employee.mail">
        <Input data-testid="employee-form-mail" disabled={disabled} value={draft.mail}
          onChange={(e) => setDraft({ ...draft, mail: e.target.value })} />
      </FormField>
      <FormField label={t("employees.position")} feedPath="Dealer.Employees.Employee.pos">
        <Input data-testid="employee-form-pos" disabled={disabled} value={draft.pos}
          onChange={(e) => setDraft({ ...draft, pos: e.target.value })} />
      </FormField>
      <FormField label={t("employees.languages")} feedPath="Dealer.Employees.Employee.langs">
        <Input data-testid="employee-form-langs" disabled={disabled} value={draft.langs}
          onChange={(e) => setDraft({ ...draft, langs: e.target.value })} />
      </FormField>
      <FormField label={t("employees.photoUrl")} feedPath="Dealer.Employees.Employee.pic" className="md:col-span-2">
        <Input data-testid="employee-form-pic" disabled={disabled} className="font-mono text-xs" value={draft.pic}
          onChange={(e) => setDraft({ ...draft, pic: e.target.value })} />
      </FormField>
      <FormField label={t("employees.largePhotoUrl")} feedPath="Dealer.Employees.Employee.pic_big" className="md:col-span-3">
        <Input data-testid="employee-form-pic-big" disabled={disabled} className="font-mono text-xs" value={draft.pic_big}
          onChange={(e) => setDraft({ ...draft, pic_big: e.target.value })} />
      </FormField>
    </div>
  );
}

function EmployeeThumbnail({ emp, onOpenFullSize }) {
  return emp.local_thumbnail_url ? (
    <button
      type="button"
      data-testid={`employee-thumbnail-${emp.id}`}
      onClick={() => onOpenFullSize(emp)}
      className="h-14 w-14 shrink-0 cursor-zoom-in rounded-md border border-border transition-opacity hover:opacity-80"
      title="View full size"
    >
      <img
        src={`${process.env.REACT_APP_BACKEND_URL}${emp.local_thumbnail_url}`}
        alt=""
        className="h-full w-full rounded-md object-cover"
      />
    </button>
  ) : (
    <div
      data-testid={`employee-thumbnail-empty-${emp.id}`}
      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border border-dashed border-border bg-muted/30 text-muted-foreground"
    >
      <ImageOff className="h-5 w-5" strokeWidth={1.5} />
    </div>
  );
}

function EmployeePhotoMeta({ emp, t }) {
  if (!emp.remote_photo_filename && !emp.local_photo_filename) return null;
  return (
    <div className="mt-1 space-y-0.5 font-mono text-[10px] text-muted-foreground" data-testid={`employee-photo-meta-${emp.id}`}>
      {emp.remote_photo_filename && (
        <p className="truncate">{t("employees.photoOld")}: {emp.remote_photo_filename}</p>
      )}
      {emp.local_photo_filename && (
        <p className="truncate">{t("employees.photoNew")}: {emp.local_photo_filename}</p>
      )}
    </div>
  );
}

/** Dealer staff list — synced from the feed every import run (upsert by
 * name, soft-deleted on absence). Deleting/editing a feed-synced row here is
 * a local override: if they're still in the feed, the next import run will
 * re-create them under the same name. Photos are downloaded by the import
 * pipeline (see backend/employee_photos.py) but can also be uploaded/deleted
 * by hand here, or (MHQ only) force-refreshed from the feed. */
export function EmployeesCard({ cardVersion, employees, dealerId, onEmployeesChanged, onDealerVersionChanged, isMhq }) {
  const { t } = useI18n();
  const [editingId, setEditingId] = useState(null); // employee id, or "new"
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [photoBusyId, setPhotoBusyId] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadTargetId, setUploadTargetId] = useState(null);
  const [maxSizeMb, setMaxSizeMb] = useState(DEFAULT_MAX_SIZE_MB);
  const [allowedFormats, setAllowedFormats] = useState(DEFAULT_ALLOWED_FORMATS);
  const [lightboxEmp, setLightboxEmp] = useState(null);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [sizeRes, formatsRes, departmentsRes] = await Promise.all([
          client.get("/settings/photo_max_size_mb"),
          client.get("/settings/photo_allowed_formats"),
          client.get("/departments"),
        ]);
        if (sizeRes.data?.value != null) setMaxSizeMb(Number(sizeRes.data.value));
        if (Array.isArray(formatsRes.data?.value)) setAllowedFormats(formatsRes.data.value);
        if (Array.isArray(departmentsRes.data)) setDepartments(departmentsRes.data);
      } catch {
        // Not readable/configured — keep the defaults.
      }
    })();
  }, []);

  const startEdit = (emp) => {
    setDraft({
      name: emp.name, title: emp.title, department: emp.department, department_id: emp.department_id || "",
      phone: emp.phone, mail: emp.mail,
      pos: emp.pos || "", pic: emp.pic || "", pic_big: emp.pic_big || "", langs: emp.langs || "",
    });
    setEditingId(emp.id);
  };

  const startAdd = () => {
    setDraft(EMPTY_DRAFT);
    setEditingId("new");
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
  };

  const save = async () => {
    if (!draft.name.trim()) {
      toast.error(t("employees.nameRequired"));
      return;
    }
    setSaving(true);
    try {
      if (editingId === "new") {
        const { data } = await client.post(`/dealers/${dealerId}/employees`, draft);
        onEmployeesChanged((prev) => [...prev, data]);
      } else {
        const { data } = await client.patch(`/employees/${editingId}`, draft);
        onEmployeesChanged((prev) => prev.map((e) => (e.id === data.id ? data : e)));
      }
      onDealerVersionChanged?.();
      toast.success(t("common.saved"));
      cancel();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (emp) => {
    if (!window.confirm(t("employees.removeConfirm", emp.name))) return;
    try {
      await client.delete(`/employees/${emp.id}`);
      onEmployeesChanged((prev) => prev.filter((e) => e.id !== emp.id));
      onDealerVersionChanged?.();
      toast.success(t("employees.removed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("employees.deleteFailed"));
    }
  };

  const applyEmployeeUpdate = (updated) => {
    onEmployeesChanged((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
    onDealerVersionChanged?.();
  };

  const triggerUpload = (emp) => {
    setUploadTargetId(emp.id);
    fileInputRef.current?.click();
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    const employeeId = uploadTargetId;
    e.target.value = "";
    if (!file || !employeeId) return;
    if (file.size > maxSizeMb * 1024 * 1024) {
      toast.error(`${t("common.fileTooLarge")} (max ${maxSizeMb} MB)`);
      return;
    }
    if (allowedFormats.length > 0 && !allowedFormats.some((f) => FORMAT_MIME[f] === file.type)) {
      const names = allowedFormats.map((f) => f.toUpperCase()).join(", ");
      toast.error(`Unsupported format — allowed: ${names}`);
      return;
    }
    setPhotoBusyId(employeeId);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await client.post(`/employees/${employeeId}/photo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      applyEmployeeUpdate(data);
      toast.success(t("employees.photoUploaded"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("employees.photoUploadFailed"));
    } finally {
      setPhotoBusyId(null);
    }
  };

  const deletePhoto = async (emp) => {
    if (!window.confirm(t("employees.deletePhotoConfirm", emp.name))) return;
    setPhotoBusyId(emp.id);
    try {
      await client.delete(`/employees/${emp.id}/photo`);
      applyEmployeeUpdate({
        ...emp, photo_source: "", remote_photo_filename: "", remote_photo_url: "",
        local_photo_filename: "", local_photo_original_url: "", local_photo_url: "", local_thumbnail_url: "",
      });
      toast.success(t("employees.photoDeleted"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("employees.photoDeleteFailed"));
    } finally {
      setPhotoBusyId(null);
    }
  };

  const forceOverwrite = async (emp) => {
    if (!window.confirm(t("employees.forceOverwriteConfirm", emp.name))) return;
    setPhotoBusyId(emp.id);
    try {
      const { data } = await client.post(`/employees/${emp.id}/photo/refresh-from-feed`);
      applyEmployeeUpdate(data);
      toast.success(t("employees.forceOverwriteDone"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("employees.forceOverwriteFailed"));
    } finally {
      setPhotoBusyId(null);
    }
  };

  const renderPhotoActions = (emp) => {
    const busy = photoBusyId === emp.id;
    return (
      <div className="mt-2 flex flex-wrap gap-2">
        <Button
          variant="outline" size="sm" className="gap-1.5 text-xs" disabled={busy}
          data-testid={`employee-upload-photo-${emp.id}`}
          onClick={() => triggerUpload(emp)}
        >
          <Upload className="h-3 w-3" />
          {emp.local_photo_url ? t("employees.replacePhoto") : t("employees.uploadPhoto")}
        </Button>
        {emp.local_photo_url && (
          <Button
            variant="outline" size="sm" className="gap-1.5 text-xs text-destructive" disabled={busy}
            data-testid={`employee-delete-photo-${emp.id}`}
            onClick={() => deletePhoto(emp)}
          >
            <Trash2 className="h-3 w-3" />
            {t("employees.deletePhoto")}
          </Button>
        )}
        {isMhq && (
          <Button
            variant="outline" size="sm" className="gap-1.5 text-xs" disabled={busy || !emp.remote_photo_filename}
            title={!emp.remote_photo_filename ? t("employees.forceOverwriteNoFeedPhoto") : undefined}
            data-testid={`employee-force-overwrite-${emp.id}`}
            onClick={() => forceOverwrite(emp)}
          >
            <RefreshCw className="h-3 w-3" />
            {t("employees.forceOverwrite")}
          </Button>
        )}
      </div>
    );
  };

  return (
    <>
    <Card className="mb-6 p-6" data-testid="employees-card">
      <input
        ref={fileInputRef}
        type="file"
        accept={allowedFormats.map((f) => FORMAT_MIME[f]).filter(Boolean).join(",") || "image/*"}
        className="hidden"
        onChange={onFileChosen}
      />
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <h2 className="flex items-center gap-1.5 text-lg font-semibold">{t("employees.title", employees.length)} <VersionTag version={cardVersion} testId="version-tag-employees" /></h2>
        </div>
        {editingId === null && (
          <Button data-testid="add-employee-btn" variant="ghost" size="sm" className="gap-1.5" onClick={startAdd}>
            <Plus className="h-3.5 w-3.5" />
            {t("employees.addEmployee")}
          </Button>
        )}
      </div>

      {editingId === "new" && (
        <div className="mb-4 rounded-md border border-primary/30 bg-primary/5 p-3">
          <EmployeeForm draft={draft} setDraft={setDraft} disabled={saving} departments={departments} />
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={cancel}>{t("common.cancel")}</Button>
            <Button data-testid="save-new-employee-btn" size="sm" onClick={save} disabled={saving} className="gap-1.5">
              <Save className="h-3.5 w-3.5" />
              {t("common.save")}
            </Button>
          </div>
        </div>
      )}

      {employees.length === 0 && editingId !== "new" && (
        <p className="py-8 text-center text-sm text-muted-foreground" data-testid="employees-empty">
          {t("employees.empty")}
        </p>
      )}

      <div className="space-y-3">
        {employees.map((emp) => (
          <div key={emp.id} className="rounded-md border border-border p-3" data-testid={`employee-row-${emp.id}`}>
            {editingId === emp.id ? (
              <>
                <div className="mb-3 flex items-start gap-3">
                  <EmployeeThumbnail emp={emp} onOpenFullSize={setLightboxEmp} />
                  <div className="min-w-0 flex-1">
                    <EmployeePhotoMeta emp={emp} t={t} />
                    {renderPhotoActions(emp)}
                  </div>
                </div>
                <EmployeeForm draft={draft} setDraft={setDraft} disabled={saving} departments={departments} />
                <div className="mt-3 flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={cancel}>{t("common.cancel")}</Button>
                  <Button data-testid={`save-employee-${emp.id}`} size="sm" onClick={save} disabled={saving} className="gap-1.5">
                    <Save className="h-3.5 w-3.5" />
                    {t("common.save")}
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <EmployeeThumbnail emp={emp} onOpenFullSize={setLightboxEmp} />
                  <div>
                    <p className="font-medium">{emp.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {[emp.title, emp.department, emp.pos].filter(Boolean).join(" · ")}
                    </p>
                    <p className="mt-1 font-mono text-xs text-muted-foreground">
                      {[emp.phone, emp.mail, emp.langs].filter(Boolean).join(" · ")}
                    </p>
                    <EmployeePhotoMeta emp={emp} t={t} />
                  </div>
                </div>
                {editingId === null && (
                  <div className="flex shrink-0 gap-1">
                    <Button variant="ghost" size="icon" data-testid={`edit-employee-${emp.id}`} onClick={() => startEdit(emp)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" data-testid={`delete-employee-${emp.id}`} onClick={() => remove(emp)}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>

    <Dialog open={!!lightboxEmp} onOpenChange={(open) => !open && setLightboxEmp(null)}>
      <DialogContent className="max-w-3xl" data-testid="employee-photo-lightbox">
        <DialogTitle>{lightboxEmp?.name}</DialogTitle>
        {lightboxEmp && (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${lightboxEmp.local_photo_url || lightboxEmp.local_thumbnail_url}`}
            alt=""
            className="max-h-[75vh] w-full rounded-md object-contain"
          />
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}

